import { ArrowRightOutlined, BoxPlotFilled } from '@ant-design/icons'
import { useProducts } from '@api/useProducts'
import { useTankTransition } from '@api/useTankTransition'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { BulkChangeBar } from '@components/Admin/TankTransition/BulkChangeBar'
import { BulkChangeDrawer } from '@components/Admin/TankTransition/BulkChangeDrawer'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { BBDTag, GraviGrid, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { formatters } from '@utils/intl/dateTimeFormatters'
import queryString from 'query-string'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'tank-transition'

export function TankTransition() {
  const grabDefaultFilters = () => {
    const defaultFilter = {}
    const storeNumber = queryString.parse(window.location.search).store_number
    const tankId = queryString.parse(window.location.search).tank_id
    if (storeNumber) defaultFilter.store_number = storeNumber
    if (tankId) defaultFilter.tank_id = parseInt(tankId)
    return defaultFilter
  }

  const [filters, setFilters] = useState(grabDefaultFilters())
  const { useTankTransitionOverviewQuery, removeTransitionMutation } = useTankTransition()
  const { mutateAsync } = removeTransitionMutation({
    afterSuccess: () => {
      gridAPIRef?.current?.redrawRows()
    },
  })
  const { data: tankTransitionData, isFetching } = useTankTransitionOverviewQuery(filters)
  const { useProductNamesQuery } = useProducts()
  const { data: productNames } = useProductNamesQuery()
  const [selectedRows, setSelectedRows] = useState([])
  const [isBulkChangeVisible, setIsBulkChangeVisible] = useState(false)
  const gridAPIRef = useRef(null)
  const queryClient = useQueryClient()
  const refreshAndDeselect = () => {
    queryClient
      .invalidateQueries({
        queryKey: ['tankTransitionOverview'],
      })
      .then(() => {
        gridAPIRef?.current?.deselectAll()
        gridAPIRef?.current?.redrawRows()
      })
  }
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.site_write || scopes.admin?.tank_transition_write
  const { updateSetting } = useUserSettings()

  const getRowId = useCallback((row) => row.data?.key, [])
  const columnDefs = useMemo(() => getColumnDefs({ removeMutation: removeTransitionMutation.mutateAsync, canEdit }), [])
  const agPropsOverrides = useMemo(
    () => ({
      rowSelection: canEdit ? 'multiple' : null,
      groupSelectsFiltered: true,
      groupSelectsChildren: true,
      suppressCellFocus: true,
      rowMultiSelectWithClick: true,
      enableRangeSelection: false,
      getRowId,
    }),
    []
  )
  return (
    <Vertical>
      <Vertical flex='1'>
        <GraviGrid
          onSelectionChanged={(props) => {
            setSelectedRows(props.api.getSelectedRows())
          }}
          externalRef={gridAPIRef}
          controlBarProps={{
            title: 'Tank Transitions',
            filters,
            setFilters,
            serverParams: tankTransitionData?.params,
          }}
          loading={isFetching}
          agPropOverrides={agPropsOverrides}
          columnDefs={columnDefs}
          storageKey={STORAGE_KEY}
          rowData={tankTransitionData?.rows || []}
          onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
          {...gridViewManager(STORAGE_KEY, updateSetting)}
        />
      </Vertical>
      <Vertical flex='0' style={{ minHeight: 100 }}>
        <BulkChangeBar
          setIsBulkChangeVisible={setIsBulkChangeVisible}
          selectedRows={selectedRows}
          refetch={refreshAndDeselect}
          removeMutation={mutateAsync}
          gridAPIRef={gridAPIRef}
          canEdit={canEdit}
        />
      </Vertical>
      <BulkChangeDrawer
        setIsBulkChangeVisible={setIsBulkChangeVisible}
        isBulkChangeVisible={isBulkChangeVisible}
        columnDefs={columnDefs}
        selectedRows={selectedRows}
        refreshAndDeselect={refreshAndDeselect}
        productNames={productNames}
      />
    </Vertical>
  )
}

const getColumnDefs = ({ canEdit }) => [
  {
    checkboxSelection: canEdit,
    headerCheckboxSelection: canEdit,
    headerCheckboxSelectionFilteredOnly: true,
    minWidth: 170,
    headerName: 'Is Scheduled',
    field: 'has_transition',
    cellRenderer: ({ value }) => {
      return (
        <div>
          {value && (
            <BBDTag warning>
              <BoxPlotFilled className='pr-2' />
              Scheduled
            </BBDTag>
          )}
        </div>
      )
    },
  },
  {
    width: 100,
    flex: 0,
    field: 'store_number',
    headerName: 'Site',
    cellRenderer: ({ value }) => <b>{value}</b>,
    comparator: (a, b) => a - b,
  },
  {
    field: 'tank_id',
    width: 100,
    flex: 0,
    headerName: 'Tank',
    cellRenderer: ({ value }) => <BBDTag theme2>{value}</BBDTag>,
  },

  {
    field: 'city',
    headerName: 'City',
  },
  {
    field: 'state',
    headerName: 'State',
    width: 80,
    flex: 0,
  },
  {
    field: 'dispatch_product',
    minWidth: 250,
    headerName: 'Dispatch Product',
    cellRenderer: ({ value, data }) => {
      if (data.has_transition) {
        return (
          <div>
            {value} <ArrowRightOutlined className='pl-2 pr-2' />
            <span className='transition-value'>{data.tank_transition.dispatch_product}</span>
          </div>
        )
      }
      return <div>{value}</div>
    },
  },
  {
    field: 'dispatch_effective',
    ...defaultDateDef('dispatch_effective'),
    headerName: 'Dispatch Effective',
    cellRenderer: ({ value, data }) => {
      if ((data.has_transition && !data.tank_transition?.dispatch_date) || (!data.has_transition && !value)) {
        return 'N/A'
      }
      return (
        <Texto weight='bold' appearance={data.has_transition ? 'warning' : 'default'}>
          {data.has_transition ? formatters.DATE(data.tank_transition?.dispatch_date) : formatters.DATE(value)}
        </Texto>
      )
    },
  },
  {
    field: 'customer_product',
    headerName: 'Customer Product',
    minWidth: 250,
    cellRenderer: ({ value, data }) => {
      if (data.has_transition) {
        return (
          <div>
            {value} <ArrowRightOutlined className='pl-2 pr-2' />
            <span className='transition-value'>{data.tank_transition?.customer_product}</span>
          </div>
        )
      }
      return <div>{value}</div>
    },
  },

  {
    field: 'customer_effective',
    headerName: 'Customer Effective',
    ...defaultDateDef('customer_effective'),
    cellRenderer: ({ value, data }) => {
      if ((data.has_transition && !data.tank_transition?.customer_date) || (!data.has_transition && !value)) {
        return 'N/A'
      }
      return (
        <Texto weight='bold' appearance={data.has_transition ? 'warning' : 'default'}>
          {data.has_transition ? formatters.DATE(data.tank_transition?.customer_date) : formatters.DATE(value)}
        </Texto>
      )
    },
  },
  {
    headerName: 'Tank Tags',
    field: 'tank_tag',
    cellRenderer: ({ data }) => data?.tank_tag?.map((tag) => tag).join(', '),
  },
  { headerName: 'Market', field: 'market' },
  { headerName: 'Sector', field: 'sector' },
]
